import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Delete,
  Add
} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import ModalResult from '../../components/ModalResult';
import ModalConfirm from '../../components/ModalConfirm';
import { useAdmin } from '../../contexts/AdminContext';
import ModalMembership from './ModalMembership';

const formatDate = (date) => {
  if (!date) return 'N/A';
  return dayjs(date).locale('it').format('DD-MMM-YY').toLowerCase();
};

const getStatusColor = (status) => {
  switch (status) {
    case 'published': return 'success';
    case 'draft': return 'warning';
    default: return 'default';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'published': return 'Pubblicata';
    case 'draft': return 'Bozza';
    default: return status;
  }
};

const defaultForm = {
  name: '',
  description: '',
  amount: '',
  currency: 'EUR',
  startDate: '',
  endDate: '',
  status: 'draft'
};

const MembershipsTable = () => {
  const { membershipList, addMembershipList, updateMembershipListItem, deleteMembershipList } = useAdmin();
  const [modal, setModal] = useState({ open: false, mode: 'add', membership: null });
  const [form, setForm] = useState(defaultForm);
  const [resultModal, setResultModal] = useState({ open: false, message: '' });
  const [confirmModal, setConfirmModal] = useState({ open: false, membership: null });
  const [openViewModal, setOpenViewModal] = useState(false);
  const [viewMembership, setViewMembership] = useState(null);

  // const refreshMemberships = async () => {
  //   try {
  //     const res = await apiClient.get('/api/membership-list');
  //     setMemberships(res.data.data || []);
  //   } catch (e) {
  //     // opzionale: gestisci errore
  //   }
  // };

  const handleOpenModal = (mode, membership = null) => {
    setModal({ open: true, mode, membership });
    setForm(membership ? {
      name: membership.name,
      description: membership.description,
      amount: membership.amount,
      currency: membership.currency,
      startDate: membership.startDate ? dayjs(membership.startDate).format('YYYY-MM-DD') : '',
      endDate: membership.endDate ? dayjs(membership.endDate).format('YYYY-MM-DD') : '',
      status: membership.status
    } : defaultForm);
  };
  const handleCloseModal = () => { setModal({ open: false, mode: 'add', membership: null }); setForm(defaultForm); };

  const handleShowResult = (message) => setResultModal({ open: true, message });
  const handleCloseResult = () => setResultModal({ open: false, message: '' });

  const handleDeleteMembership = async () => {
    if (!confirmModal.membership) return;
    try {
      const result = await deleteMembershipList(confirmModal.membership._id);
      if (result.success) {
        handleShowResult('Tesseramento eliminato con successo!');
      } else {
        handleShowResult('Errore durante la cancellazione del tesseramento');
      }
    } catch (e) {
      handleShowResult('Errore durante la cancellazione del tesseramento');
    } finally {
      handleCloseConfirm();
    }
  };
  const handleOpenConfirm = (membership) => setConfirmModal({ open: true, membership });
  const handleCloseConfirm = () => setConfirmModal({ open: false, membership: null });

  const handleOpenViewModal = (membership) => { setViewMembership(membership); setOpenViewModal(true); };
  const handleCloseViewModal = () => { setViewMembership(null); setOpenViewModal(false); };


  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modal.mode === 'add') {
        const result = await addMembershipList(form);
        if (result.success) {
          handleShowResult('Tesseramento aggiunto con successo!');
        } else {
          handleShowResult('Errore durante il salvataggio del tesseramento');
        }
      } else {
        // console.log('form', form);
        // console.log('modal.membership', modal.membership);
        const result = await updateMembershipListItem({
          _id: modal.membership._id,
          ...form
        });
        if (result.success) {
          handleShowResult('Tesseramento modificato con successo!');
        } else {
          handleShowResult('Errore durante il salvataggio del tesseramento');
        }
      }
      handleCloseModal();
    } catch (e) {
      handleShowResult('Errore durante il salvataggio del tesseramento');
    }
  };

  const columns = [
    { field: 'name', headerName: 'Nome', flex: 1, minWidth: 140, resizable: true },
    // { field: 'description', headerName: 'Descrizione', flex: 1.5, minWidth: 180, resizable: true },
    { field: 'amount', headerName: 'Importo', flex: 0.7, minWidth: 90, resizable: true, renderCell: (params) => `€${params.value}` },
    { field: 'currency', headerName: 'Valuta', flex: 0.5, minWidth: 70, resizable: true },
    { field: 'startDate', headerName: 'Data Inizio', flex: 0.8, minWidth: 110, resizable: true, renderCell: (params) => formatDate(params.value) },
    { field: 'endDate', headerName: 'Data Fine', flex: 0.8, minWidth: 110, resizable: true, renderCell: (params) => formatDate(params.value) },
    { field: 'status', headerName: 'Stato', flex: 0.7, minWidth: 100, resizable: true, renderCell: (params) => (
      <Chip label={getStatusLabel(params.value)} color={getStatusColor(params.value)} size="small" />
    ) },
    {
      field: 'actions',
      headerName: 'Azioni',
      flex: 1, minWidth: 120, resizable: true,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" color="primary" onClick={() => handleOpenViewModal(params.row)}>
            <Visibility />
          </IconButton>
          <IconButton size="small" color="primary" onClick={() => handleOpenModal('edit', params.row)}>
            <Edit />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleOpenConfirm(params.row)}>
            <Delete />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Gestione Tesseramenti</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal('add')}>
          Aggiungi Campagna
        </Button>
      </Box>
      <DataGrid
        rows={membershipList.map(m => ({ ...m, id: m._id }))}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
        }}
        rowsPerPageOptions={[10, 25, 50]}
        autoHeight
        disableSelectionOnClick
        hideFooterPagination={membershipList.length <= 10}
      />
      {/* Modal per aggiunta/modifica/visualizzazione */}
      <ModalMembership
        open={modal.open || openViewModal}
        onClose={() => { handleCloseModal(); handleCloseViewModal(); }}
        onSubmit={handleFormSubmit}
        mode={openViewModal ? 'view' : modal.mode}
        form={form}
        setForm={setForm}
        membership={openViewModal ? viewMembership : modal.membership}
      />
      {/* Modale conferma eliminazione */}
      <ModalConfirm open={confirmModal.open} onClose={handleCloseConfirm} onConfirm={handleDeleteMembership} title="Conferma eliminazione" description={`Sei sicuro di voler eliminare il tesseramento "${confirmModal.membership?.name}"?`} />
      {/* Modale risultato */}
      <ModalResult open={resultModal.open} onClose={handleCloseResult} message={resultModal.message} />
    </Box>
  );
};

export default MembershipsTable; 